import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { RefreshCw } from "lucide-react";
import { functions } from "../firebase";

/* Orders recorded by the payment webhook.
 *
 * Reads go through the listOrders callable rather than client-side Firestore so
 * buyer names, emails and phone numbers are never readable from the browser. */

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "All time", days: null },
];

const rm = (sen) =>
  `RM ${((Number(sen) || 0) / 100).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const when = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-MY", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const STATUS_CLASS = {
  captured: "badge-success",
  authorized: "badge-warning",
  failed: "badge-error",
};

export default function OrdersTable({ token }) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(30);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listOrders = httpsCallable(functions, "listOrders");
      const res = await listOrders({
        token,
        limit: 500,
        sinceMs: rangeDays ? Date.now() - rangeDays * 86400000 : null,
      });
      const data = res.data || {};
      if (!data.ok) throw new Error(data.error || "Failed to load orders");
      setOrders(data.orders || []);
      setStats(data.stats || null);
    } catch (e) {
      toast.error(String((e && e.message) || e));
    } finally {
      setLoading(false);
    }
  }, [token, rangeDays]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!q) return true;
      return [o.name, o.email, o.contact, o.promoCode, o.paymentId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orders, query, statusFilter]);

  return (
    <div className="space-y-4">
      {/* ---- Summary ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Revenue" value={stats ? rm(stats.revenue) : "—"} hint="captured only" />
        <Stat label="Paid orders" value={stats ? stats.paidCount : "—"} />
        <Stat
          label="Average order"
          value={stats ? rm(stats.averageOrderValue) : "—"}
        />
        <Stat
          label="Attempts"
          value={stats ? stats.totalCount : "—"}
          hint="incl. failed"
        />
      </div>

      {stats && (Object.keys(stats.byMethod).length > 0 || Object.keys(stats.byPromo).length > 0) && (
        <div className="flex flex-wrap gap-4 text-sm">
          {Object.keys(stats.byMethod).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="opacity-60">Methods:</span>
              {Object.entries(stats.byMethod).map(([m, n]) => (
                <span key={m} className="badge badge-outline">
                  {m} · {n}
                </span>
              ))}
            </div>
          )}
          {Object.keys(stats.byPromo).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="opacity-60">Promos:</span>
              {Object.entries(stats.byPromo).map(([c, n]) => (
                <span key={c} className="badge badge-outline">
                  {c} · {n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Controls ---- */}
      <div className="flex gap-2 flex-wrap items-center">
        {/* Search takes its own line on a phone; the two filters then split
            the next one instead of each landing on a line of its own. */}
        <input
          className="input input-bordered input-sm w-full sm:flex-1 sm:w-auto sm:min-w-[12rem]"
          placeholder="Search name, email, phone, code…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="select select-bordered select-sm flex-1 sm:flex-none"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="captured">Captured</option>
          <option value="authorized">Authorized</option>
          <option value="failed">Failed</option>
        </select>
        <select
          className="select select-bordered select-sm flex-1 sm:flex-none"
          value={String(rangeDays)}
          onChange={(e) =>
            setRangeDays(e.target.value === "null" ? null : Number(e.target.value))
          }
        >
          {RANGES.map((r) => (
            <option key={r.label} value={String(r.days)}>
              {r.label}
            </option>
          ))}
        </select>
        <button className="btn btn-sm btn-ghost" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ---- Table ---- */}
      {loading ? (
        <div className="flex items-center justify-center p-10">
          <span className="loading loading-spinner" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center p-10 opacity-60">
          No orders in this range yet.
        </div>
      ) : (
        <>
          {/* Six columns don't fit a phone, and a sideways-scrolling table
              hides the amount and status — the two columns anyone opens this
              on a phone to check. Below sm each order is a card instead. */}
          <div className="space-y-2 sm:hidden">
            {visible.map((o) => (
              <div key={o.paymentId} className="card bg-base-100 shadow">
                <div className="card-body p-3 gap-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{o.name || "—"}</div>
                      <div className="text-xs opacity-60 break-all">
                        {o.email || "—"}
                      </div>
                      {o.contact && (
                        <div className="text-xs opacity-60">{o.contact}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold whitespace-nowrap">
                        {rm(o.amount)}
                      </div>
                      <span
                        className={`badge badge-sm ${STATUS_CLASS[o.status] || "badge-ghost"}`}
                      >
                        {o.status || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-60">
                    <span>{when(o.createdAt)}</span>
                    <span>
                      {o.method || "—"}
                      {(o.bank || o.wallet) && ` · ${o.bank || o.wallet}`}
                    </span>
                    {o.promoCode && <span>Promo {o.promoCode}</span>}
                  </div>
                  {o.errorDescription && (
                    <div className="text-xs opacity-60">{o.errorDescription}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>When</th>
                <th>Buyer</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Promo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr key={o.paymentId}>
                  <td className="whitespace-nowrap">{when(o.createdAt)}</td>
                  <td>
                    <div className="font-medium">{o.name || "—"}</div>
                    <div className="text-xs opacity-60">{o.email || "—"}</div>
                    <div className="text-xs opacity-60">{o.contact || ""}</div>
                  </td>
                  <td className="whitespace-nowrap">{rm(o.amount)}</td>
                  <td>
                    {o.method || "—"}
                    {(o.bank || o.wallet) && (
                      <div className="text-xs opacity-60">{o.bank || o.wallet}</div>
                    )}
                  </td>
                  <td>{o.promoCode || "—"}</td>
                  <td>
                    <span
                      className={`badge badge-sm ${STATUS_CLASS[o.status] || "badge-ghost"}`}
                    >
                      {o.status || "—"}
                    </span>
                    {o.errorDescription && (
                      <div className="text-xs opacity-60 max-w-[16rem]">
                        {o.errorDescription}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="card bg-base-100 shadow">
      <div className="card-body p-4">
        <div className="text-xs opacity-60">{label}</div>
        <div className="text-xl font-bold">{value}</div>
        {hint && <div className="text-xs opacity-50">{hint}</div>}
      </div>
    </div>
  );
}
